import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import DataGrid, { Filter } from '#/common/components/DataGrid';
import MatchItemsModal from '#/common/components/MatchItemsModal';
import type { Location } from '#/graphql/types';
import { gql } from '@apollo/client';
import { getLocationList } from '#/graphql/queries';
import { createMatchLocationColumns } from './columns';
import { matchStatuses } from '../../../utils/constants';
import { actions } from '../LocationDataGrid/actions';
import useToast from '#/common/hooks/useToast';
import { Level } from '../Location.Mappings';

const getLocationListQuery = gql(getLocationList);

export interface MatchLocationsModalProps {
  visible: boolean;
  location?: Location;
  isSubmitting?: boolean;
  onClose: () => void;
  onConfirm: (selectedTisLocationId: string | null) => Promise<void> | void;
}

const MatchLocationsModal: React.FC<MatchLocationsModalProps> = ({
  visible,
  location,
  isSubmitting,
  onClose,
  onConfirm,
}) => {
  const { alert } = useToast();
  const selectedTisLocationIdRef = useRef<string | null>(null);
  const matchCandidatesGridRef = useRef<any>(null);

  const getSelectedLocationId = useCallback(() => selectedTisLocationIdRef.current, []);

  const isDefaultMatchedCandidate = useCallback(
    (candidateLocation: Location) => {
      const isArchived =
        String(candidateLocation?.matchStatus || '').toLowerCase() ===
        matchStatuses.archived.key.toLowerCase();

      return (
        !!location?.thirdPartyId &&
        candidateLocation?.thirdPartyId === location.thirdPartyId &&
        isArchived &&
        candidateLocation?.id !== location?.id
      );
    },
    [location?.id, location?.thirdPartyId],
  );

  const getDefaultMatchedCandidateIdFromVisibleRows = useCallback(() => {
    const visibleRows = matchCandidatesGridRef.current?.instance
      ?.getVisibleRows?.()
      ?.map((row: any) => row?.data as Location)
      ?.filter(Boolean);

    const defaultSelectedCandidate = visibleRows?.find(isDefaultMatchedCandidate);
    return defaultSelectedCandidate?.id || null;
  }, [isDefaultMatchedCandidate]);

  const applyDefaultSelection = useCallback(() => {
    if (!visible || selectedTisLocationIdRef.current) {
      return;
    }

    const defaultMatchedCandidateId = getDefaultMatchedCandidateIdFromVisibleRows();
    if (!defaultMatchedCandidateId) {
      return;
    }

    selectedTisLocationIdRef.current = defaultMatchedCandidateId;
  }, [getDefaultMatchedCandidateIdFromVisibleRows, visible]);

  useEffect(() => {
    selectedTisLocationIdRef.current = null;
  }, [location?.id, visible]);

  useEffect(() => {
    return () => {
      matchCandidatesGridRef.current?.instance?.off?.('contentReady', applyDefaultSelection);
    };
  }, [applyDefaultSelection]);

  const handleSelectLocationId = useCallback((id: string) => {
    selectedTisLocationIdRef.current = id;

    // Repaint rows so custom radio cells re-evaluate checked state without reloading data.
    matchCandidatesGridRef.current?.instance?.repaint?.();
  }, []);

  const handleRowClick = useCallback(
    (e: any) => {
      const rowId = e?.data?.id;
      if (rowId) {
        handleSelectLocationId(rowId);
      }
    },
    [handleSelectLocationId],
  );

  const matchCandidatesPermanentFilters = useMemo(() => {
    const matchStatusFilter = location?.thirdPartyId
      ? [
          ['matchStatus', '=', matchStatuses.tis.key],
          'OR',
          [
            ['matchStatus', '=', matchStatuses.archived.key],
            'AND',
            ['thirdPartyId', '=', location.thirdPartyId],
          ],
        ]
      : ['matchStatus', '=', matchStatuses.tis.key];

    const filters: Filter[] = [
      ['thirdPartyId', 'isNotNull'],
      'AND',
      ['source', 'isNotNull'],
      'AND',
      ['level', '=', Level.Building],
      'AND',
      matchStatusFilter,
    ];

    if (location?.id) filters.push('AND', ['id', '<>', location.id]);

    if (location?.tenantId) filters.push('AND', ['tenantId', '=', location.tenantId]);

    return filters;
  }, [location?.id, location?.thirdPartyId, location?.tenantId]);

  const matchCandidatesColumns = useMemo(
    () =>
      createMatchLocationColumns({
        getSelectedLocationId,
        onSelectLocationId: handleSelectLocationId,
        isDefaultSelectedLocation: isDefaultMatchedCandidate,
      }),
    [getSelectedLocationId, handleSelectLocationId, isDefaultMatchedCandidate],
  );

  const handleConfirm = async () => {
    let selectedId = selectedTisLocationIdRef.current;
    console.log('Selected TC Location ID for matching:', selectedId);

    if (!selectedId) {
      selectedId = getDefaultMatchedCandidateIdFromVisibleRows();
    }

    if (!selectedId) {
      void alert('error', 'Please select a Location to match');
      return;
    }

    selectedTisLocationIdRef.current = selectedId;
    await onConfirm(selectedId);
  };

  const handleClose = () => {
    selectedTisLocationIdRef.current = null;
    onClose();
  };

  const handleClearSelection = () => {
    if (!location?.thirdPartyId) {
      void alert(
        'error',
        'The Location is not currently matched with any TC Location, so there is no selection to clear.',
      );
      return;
    }
    selectedTisLocationIdRef.current = null;
    void onConfirm(null);
  };

  return (
    <MatchItemsModal
      visible={visible}
      itemType="Location"
      item={location?.name || location?.id || '-'}
      isSubmitting={isSubmitting}
      warningMessage="All of the Location's data will be updated after matching it with a new one."
      onSecondaryAction={handleClearSelection}
      onClose={handleClose}
      onConfirm={handleConfirm}>
      <DataGrid
        id="match-locations-candidates"
        pluralText="Locations"
        singularText="Location"
        selectionMode="none"
        columns={matchCandidatesColumns}
        getItemsQuery={getLocationListQuery}
        actions={actions}
        permanentFilters={matchCandidatesPermanentFilters}
        onRowClick={handleRowClick}
        disableStateStoring
        defaultPageSize={5}
        setRef={(ref) => {
          const previousRef = matchCandidatesGridRef.current;

          previousRef?.instance?.off?.('contentReady', applyDefaultSelection);
          matchCandidatesGridRef.current = ref;

          ref?.instance?.off?.('contentReady', applyDefaultSelection);
          ref?.instance?.on?.('contentReady', applyDefaultSelection);

          applyDefaultSelection();
        }}
      />
    </MatchItemsModal>
  );
};

export default React.memo(MatchLocationsModal);
